import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { VerificationTemplate } from "../../shared/app/VerificationTemplate";
import { DeleteTemplateModal } from "../../shared/app/VerificationTemplate/modals/DeleteTemplateModal";
import { CreateTemplateModal } from "../../shared/app/VerificationTemplate/modals/CreateTemplateModal";
import { Layout } from "providers/Layout";
import { useVerificationTemplateController } from "./controller";
import {
  Template,
  ApplicantSettingsErrors,
  VerificationSchemas
} from "types/landlord/verificationTemplate";
import { LayoutHeader } from "../../shared/app/VerificationTemplate/components/LayoutHeader";
import { Loader } from "shared/components/Loader";
import { useQuery } from "@tanstack/react-query";
import templatesApi from "api/landlord/templates";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { ApplicantType, VerificationNames } from "shared/types";
import { toast } from "react-toastify";
import { useVerificationTemplatesController } from "../verification-templates/controller";
import { SideBar } from "../../shared/app/VerificationTemplate/components/SideBar";
import { LayoutFooter } from "../../shared/app/VerificationTemplate/components/LayoutFooter";
import { has } from "shared/utils/has";

export const VerificationTemplatesIdView = () => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [duplicateTemplate, setDuplicateTemplate] = useState<Template | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [applicantSettingsErrors, setApplicantSettingsErrors] =
    useState<ApplicantSettingsErrors | null>(null);

  const { defaultTemplate } = useVerificationTemplatesController();

  const {
    templateQuery,
    deleteTemplate,
    updateTemplate,
    createTemplate,
    verificationSchemas
  } = useVerificationTemplateController();

  useQuery({
    queryKey: ["DEFAULT_CRITERIA_SETTINGS"],
    queryFn: () =>
      templatesApi.getDefaultCriteria().then((defaultCriteria) => {
        if (!template?.screening_criteria)
          setTemplate((prevTemplate: Template | null) =>
            prevTemplate
              ? {
                  ...prevTemplate,
                  screening_criteria: defaultCriteria
                }
              : null
          );
      }),
    enabled: Boolean(template)
  });

  const toggleEditMode = () => {
    if (isEditing && editedName !== template?.name)
      setTemplate((prevTemplate: Template | null) =>
        prevTemplate
          ? {
              ...prevTemplate,
              name: editedName
            }
          : null
      );

    setIsEditing(!isEditing);
  };

  useEffect(() => {
    setEditedName(template?.name || "");
  }, [template?.name]);

  useEffect(() => {
    if (templateQuery.data?.verification_template) {
      const currentVerificationTemplate =
        templateQuery.data.verification_template;
      if (defaultTemplate) {
        const verification_schemas = ["applicant", "co_signer"].reduce(
          (acc, prev) => {
            const initialSchemas = defaultTemplate.verification_schemas[prev];

            const currentSchemas = currentVerificationTemplate
              .verification_schemas[prev]
              ? currentVerificationTemplate.verification_schemas[prev]
              : initialSchemas;

            const requiredFieldSteps = [
              "pets",
              "emergency_contacts",
              "vehicles"
            ];
            const rentalApplicationRequired = requiredFieldSteps.reduce(
              (acc, step) => {
                const key = `${step}_step_required`;
                return {
                  ...acc,
                  [key]: {
                    ...initialSchemas.rental_application[key],
                    ...currentSchemas.rental_application[key]
                  }
                };
              },
              {}
            );

            currentSchemas.rental_application = {
              ...currentSchemas.rental_application,
              ...rentalApplicationRequired
            };

            currentSchemas.income_verification = {
              ...initialSchemas.income_verification,
              ...currentSchemas.income_verification
            };

            return {
              ...acc,
              [prev]: currentSchemas
            };
          },
          { applicant: {}, co_signer: {} }
        ) as VerificationSchemas;
        currentVerificationTemplate.verification_schemas = verification_schemas;
      }

      currentVerificationTemplate.settings = {
        ...currentVerificationTemplate.settings,
        applicant: {
          ...currentVerificationTemplate.settings.applicant,
          enabled_screening_services: true
        },
        co_signer: {
          ...currentVerificationTemplate.settings.co_signer,
          enabled_screening_services:
            currentVerificationTemplate.settings.co_signer
              .enabled_screening_services ?? true
        }
      };

      setTemplate(currentVerificationTemplate);
    }
  }, [defaultTemplate, templateQuery.data?.verification_template]);

  const { type } = useAuthContext();

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (templateQuery.isLoading)
    return (
      <Layout>
        <Loader />
      </Layout>
    );

  if (templateQuery.isError)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Error occurred finding template</h1>
        <p className="text-base">{templateQuery.error.message}</p>
      </Layout>
    );

  if (!template || !templateQuery.data?.verification_template)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Template not found</h1>
      </Layout>
    );

  const handleCancel = () => {
    navigate(`/verification-templates`);
  };

  const handleSaveClick = () => {
    if (template.recommendation_method === "rule_based") {
      const hasEmptyRuleSets = template.rule_criteria?.some(
        (criteria) => !criteria.rules.length
      );

      if (hasEmptyRuleSets) {
        toast.error("Empty rule sets are not allowed");
        return;
      }

      const hasDeclineRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "decline" && criteria.rules.length
      );
      const hasApproveRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "approve" && criteria.rules.length
      );

      if (
        !hasDeclineRule ||
        !hasApproveRule ||
        !template.rule_criteria?.length
      ) {
        toast.error(
          "Template must have at least one approve and decline rule when using rule based recommendation"
        );

        return;
      }

      // Check if all rules have values
      const hasEmptyRule = template.rule_criteria.some((criteria) =>
        criteria.rules.some((rule) => !has(rule.value) || rule.value === "")
      );

      if (hasEmptyRule) {
        toast.error("All rules must have a value");
        return;
      }
    }

    if (template.recommendation_method === "score_based") {
      const newErrors = {};

      Object.entries(
        template.screening_criteria?.applicant_settings || {}
      ).forEach(([key, entry]) => {
        if (entry.value !== 0 && !entry.value)
          newErrors[key] = "This value is required";
      });

      setApplicantSettingsErrors(newErrors);

      if (Object.keys(newErrors).length) {
        toast.error("All criteria must have a value");
        return;
      }
    }

    const updatedTemplate: Template = {
      ...template,
      settings:
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !Object.keys(template.settings.applicant || {}).length &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !Object.keys(template.settings.co_signer || {}).length
          ? {
              applicant: {
                ...template.settings.applicant,
                identity_verification: {
                  selfie_check: true,
                  prevent_apply_if_failed: true
                }
              },
              co_signer: {
                ...template.settings.co_signer,
                identity_verification: {
                  selfie_check: true,
                  prevent_apply_if_failed: true
                }
              }
            }
          : template.settings,
      name: editedName
    };

    updateTemplate(updatedTemplate);

    // Clear modals
    setShowDeleteModal(false);
    setShowDuplicateModal(false);
  };

  return (
    <>
      <div className="bg-background-900">
        <VerificationTemplate
          layoutProps={{
            header: <LayoutHeader />,
            sidebar: (
              <SideBar
                handleDuplicate={() => {
                  setDuplicateTemplate({ ...template });
                  setShowDuplicateModal(true);
                }}
                isEditing={isEditing}
                template={template}
                editedName={editedName}
                setEditedName={setEditedName}
                toggleEditMode={toggleEditMode}
                setShowDeleteModal={setShowDeleteModal}
              />
            ),
            footer: (
              <LayoutFooter
                onCancel={handleCancel}
                onSave={() => {
                  if (!template.name) toast.error("Template name is required");
                  handleSaveClick();
                }}
              />
            )
          }}
          verificationTemplate={template}
          savedTemplate={templateQuery.data.verification_template}
          verificationSchemas={verificationSchemas || []}
          onTemplateUpdate={setTemplate}
          onApplicantSettingsErrorsUpdate={setApplicantSettingsErrors}
          originalQualificationsDisclosureText={
            template.verification_schemas[ApplicantType.Applicant][
              VerificationNames.TermsAndConditions
            ]?.additional_text || ""
          }
          originalSubmissionDisclosureText={
            template.verification_schemas[ApplicantType.Applicant][
              VerificationNames.SubmissionDisclosure
            ]?.additional_text || ""
          }
          applicantSettingsErrors={applicantSettingsErrors}
        />
      </div>

      <CreateTemplateModal
        opened={showDuplicateModal}
        template={duplicateTemplate}
        verificationSchemas={verificationSchemas || []}
        handleCancel={() => setShowDuplicateModal(false)}
        handleSave={createTemplate}
        onTemplateUpdate={setDuplicateTemplate}
      />
      <DeleteTemplateModal
        opened={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={deleteTemplate}
      />
    </>
  );
};
